import { EventEmitter } from 'eventemitter3';
import loggerManager from '../utils/log/logger-manager';
import { IS_WORKER, IS_WORKLET, MICROPHONE_DEFAULT, NAME } from '../common/constants';
import { TIMER_TYPE, CheckPermissionType } from '../enums';
import { isMediaDevicesSupported, isUsedInHttpProtocol } from './rtc-detection';
import Timer from '../utils/timer';
import { IS_IOS, IS_SAFARI } from '../utils/environment';
import { getAudioContext, resumeContext } from '../utils/audio-context';

const findByDeviceID = (deviceId: string) => (device: DeviceInfo) => device.deviceId === deviceId;
export const enum DeviceChangeEvent {
  AUDIO_INPUT_ADDED = 'audioInputAdded',
  AUDIO_INPUT_REMOVED = 'audioInputRemoved',
  VIDEO_INPUT_ADDED = 'videoInputAdded',
  VIDEO_INPUT_REMOVED = 'videoInputRemoved',

  AUDIO_OUTPUT_ADDED = 'audioOutputAdded',
  AUDIO_OUTPUT_REMOVED = 'audioOutputRemoved',
}

class Device<T extends 'Input' | 'Output'> {
  kind: 'audio' | 'video';
  type: 'Input' | 'Output';
  devices: (T extends 'Input' ? InputDeviceInfo : MediaDeviceInfo)[] = [];
  constructor(kind: 'audio' | 'video', type: T) {
    this.kind = kind;
    this.type = type;
  }
  update(devices: (T extends 'Input' ? InputDeviceInfo : MediaDeviceInfo)[], detector?: DeviceDetector) {
    const newDevices = devices.filter((device: DeviceInfo) => device.kind === `${this.kind}${this.type.toLocaleLowerCase()}`);
    // 之前的设备列表是没有权限的情况下获取的，则不处理后续流程。避免首次授权时，抛出一堆的 DeviceAdded 事件。
    if (this.devices.length === 1 && isDeviceGotFromWithoutPermission(this.devices[0])) {
      this.devices = newDevices;
      return;
    }
    if (detector) {
      newDevices.forEach(d => {
        if (d.deviceId && !this.devices.find(findByDeviceID(d.deviceId))) {
          const eventName = `${this.kind}${this.type}Added`;
          loggerManager.warn(`${eventName}: ${JSON.stringify(d)}`);
          detector.emit(eventName, d);
        }
      });
      this.devices.forEach(d => {
        if (d.deviceId && !newDevices.find(findByDeviceID(d.deviceId))) {
          const eventName = `${this.kind}${this.type}Removed`;
          loggerManager.warn(`${eventName}: ${JSON.stringify(d)}`);
          detector.emit(eventName, d);
        }
      });
    }
    this.devices = newDevices;
  }

  hasDevice(deviceId: string) {
    return !!this.devices.find(device => device.deviceId === deviceId);
  }
}
export interface DeviceInfo {
  readonly deviceId: string;
  readonly groupId: string;
  readonly kind: MediaDeviceKind;
  readonly label: string;
}
/**
 * 输入设备探测器,全局使用
 * 主要用于检测设备变更
 * @class DeviceDetector
 */
export class DeviceDetector extends EventEmitter {
  audioInputs = new Device(NAME.AUDIO, 'Input');
  videoInputs = new Device(NAME.VIDEO, 'Input');
  audioOutputs = new Device(NAME.AUDIO, 'Output');
  constructor() {
    super();
    this.init();
    if (navigator.mediaDevices) {
      if (navigator.mediaDevices.addEventListener) {
        navigator.mediaDevices.addEventListener('devicechange', () => this.update());
      }
      // 在不支持 ondevicechange 事件的环境中，采用10s定时轮训设备列表的方式来兼容。
      if (!('ondevicechange' in navigator.mediaDevices)) {
        Timer.run(TIMER_TYPE.INTERVAL, () => {
          this.update();
        }, { delay: 10000 });
      }
    }
  }
  init() {
    getDevices().then(devices => {
      this.audioInputs.update(devices as InputDeviceInfo[]);
      this.videoInputs.update(devices as InputDeviceInfo[]);
      this.audioOutputs.update(devices);
    });
  }

  async update(checkPermission: CheckPermissionType = CheckPermissionType.NONE) {
    const newDevices = await getDevices(checkPermission);
    this.audioInputs.update(newDevices as InputDeviceInfo[], this);
    this.videoInputs.update(newDevices as InputDeviceInfo[], this);
    this.audioOutputs.update(newDevices, this);
    return this;
  }

  hasBlueTooth() {
    // outputLatency 大于 150ms 时，可能是蓝牙设备
    if (getAudioContext()?.outputLatency * 1000 > 150) return true;
    const BLUE_TOOTH_SPEAKER_KEY_WORDS = ['bluetooth', 'air', 'wireless', 'bt', 'tws', 'buds', 'headset', 'headphone'];
    return this.audioOutputs.devices.some(speaker => BLUE_TOOTH_SPEAKER_KEY_WORDS.some(blueTooth => speaker.label.toLowerCase().includes(blueTooth))) ||
    this.audioInputs.devices.some(speaker => BLUE_TOOTH_SPEAKER_KEY_WORDS.some(blueTooth => speaker.label.toLowerCase().includes(blueTooth)));
  }
}
export const deviceDetector =
  IS_WORKLET || IS_WORKER ? null : new DeviceDetector();

export function isDeviceGotFromWithoutPermission(device: DeviceInfo) {
  return device.deviceId === device.groupId && device.groupId === '';
}

export async function getDevices(checkPermission: CheckPermissionType = CheckPermissionType.NONE): Promise<MediaDeviceInfo[]> {
  if (isUsedInHttpProtocol() || !isMediaDevicesSupported()) {
    return [];
  }

  let devices = await navigator.mediaDevices.enumerateDevices();

  if (checkPermission !== CheckPermissionType.NONE) {
    // 在未授权时，无法正常获取设备列表，第一次获取设备列表后，检查如果设备列表不正常，则采集一次设备后重新获取。
    // https://tapd.woa.com/20396022/prong/stories/view/1020396022115219005
    const constraints = { audio: false, video: false };
    devices.forEach(device => {
      if (isDeviceGotFromWithoutPermission(device)) {
        if (device.kind === NAME.AUDIO_INPUT) {
          constraints.audio = true;
        } else if (device.kind === NAME.VIDEO_INPUT) {
          constraints.video = true;
        }
      }
    });
    if (checkPermission === CheckPermissionType.CAMERA) constraints.audio = false;
    if (checkPermission === CheckPermissionType.MICROPHONE) constraints.video = false;
    if (constraints.audio || constraints.video) {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (constraints.audio) resumeContext();
      } catch (error) {
        loggerManager.debug('capture before getDevices failed: ', error);
      }
      devices = await navigator.mediaDevices.enumerateDevices();
      stream?.getTracks().forEach(track => track.stop());
    }
  }
  // @ts-ignore
  return devices.map((item, index) => {
    const result = {
      kind: item.kind,
      deviceId: item.deviceId,
      groupId: item.groupId,
      label: item.label || `${item.kind}_${index}`
    };
    if (item.deviceId.length > 0) {
      historyDeviceIdSet.add(`${item.deviceId}_${item.kind}`);
    }
    // @ts-ignore
    // chrome 支持的字段 https://caniuse.com/mdn-api_inputdeviceinfo_getcapabilities
    if (item.getCapabilities) result.getCapabilities = () => item.getCapabilities();
    return result;
  });
}

export function getMicrophones(checkPermission = false) {
  return deviceDetector!.update(checkPermission ? CheckPermissionType.MICROPHONE : CheckPermissionType.NONE).then(dd => dd.audioInputs.devices);
}

export function getCameras(checkPermission = false) {
  return deviceDetector!.update(checkPermission ? CheckPermissionType.CAMERA : CheckPermissionType.NONE).then(dd => dd.videoInputs.devices);
}

let isSpeakerLogged = false;

export async function logSpeakers() {
  try {
    if (!isSpeakerLogged) {
      isSpeakerLogged = true;
      // deviceId 打印 8 位够用于定位某个设备，多余的字符无需上报，占用空间。
      loggerManager.info(`speakers:${(await getSpeakers()).map(speaker => (` ${speaker.deviceId.slice(0, 8)}: ${speaker.label}`))}`);
    }
  } catch (error) {}
}
export async function getSpeakers(checkPermission = false) {
  // 不支持获取扬声器，无需检查权限
  if (IS_IOS || IS_SAFARI) checkPermission = false;
  return deviceDetector!.update(checkPermission ? CheckPermissionType.MICROPHONE : CheckPermissionType.NONE).then(dd => dd.audioOutputs.devices);
}

// 记录所有设备的 deviceId_kind, eg: default_audioinput
// 用途：若摄像头拔出导致 videoTrack ended，此时 getCameras 可能拿不到被拔出的设备，isCameraTrack 方法需要使用到该 Set 来判断 deviceId 是否存在于摄像头列表中。
const historyDeviceIdSet = new Set();


/**
 * 判断是否是摄像头采集的 track
 *
 * @export
 * @param {MediaStreamTrack} mediaStreamTrack
 * @return {boolean}
 */
export function isCameraTrack(mediaStreamTrack: MediaStreamTrack) {
  if (mediaStreamTrack instanceof CanvasCaptureMediaStreamTrack) return false;
  if (!(mediaStreamTrack instanceof MediaStreamTrack)) return false;
  const lowerCaseLabel = mediaStreamTrack.label.toLocaleLowerCase();
  if (lowerCaseLabel.includes('camera') || lowerCaseLabel.includes('webcam')) return true;

  const settings = mediaStreamTrack?.getSettings() || {};
  const key = `${settings.deviceId}_${NAME.VIDEO_INPUT}`;
  if (historyDeviceIdSet.has(key)) {
    return true;
  }
  return false;
}

/**
 * 判断是否是麦克风采集的 track
 *
 * @export
 * @param {MediaStreamTrack} mediaStreamTrack
 * @return {boolean}
 */
export function isMicrophoneTrack(mediaStreamTrack: MediaStreamTrack) {
  if (mediaStreamTrack instanceof CanvasCaptureMediaStreamTrack) return false;
  if (!(mediaStreamTrack instanceof MediaStreamTrack)) return false;
  const lowerCaseLabel = mediaStreamTrack.label.toLocaleLowerCase();
  if (lowerCaseLabel.includes('mic') || lowerCaseLabel.includes('麦克风')) return true;

  const settings = mediaStreamTrack?.getSettings() || {};
  const key = `${settings.deviceId}_${NAME.AUDIO_INPUT}`;
  if (historyDeviceIdSet.has(key)) {
    return true;
  }
  return false;
}

/**
 * Chrome 中有特殊的 microphone，deviceId = 'default'，由浏览器抉择哪个麦克风是 default。
 * 该函数用于判断 两个麦克风是否为相同的麦克风。
 * 原理：deviceId + groupId + label 可以唯一标识一个麦克风设备。
 * 仅靠 groupId 不够，在具有麦克风阵列的 Windows 设备上，所有麦克风的 groupId 都是一样的。
 * @param {string} microphonGroupIdInUse 当前正常使用的 mic groupId
 * @param {string} label 当前正在使用 mac label
 * @return {Promise<boolean>}
 */
export async function isSameDefaultMicrophone(microphonGroupIdInUse: string, label: string) {
  // default mic 的 groupId 和当前使用的一致，则是相同麦克风
  const micList = await getMicrophones();
  const defaultMicrophone = micList.find(microphone => microphone.deviceId === MICROPHONE_DEFAULT);
  // android 自带浏览器 和 weixin webview 可能存在获取不到 label 的情况，此时只通过 deviceId + groupId 来判断是否为相同的麦克风
  if (!label && defaultMicrophone?.groupId === microphonGroupIdInUse) {
    return true;
  }
  if (defaultMicrophone?.groupId === microphonGroupIdInUse && defaultMicrophone.label === label) {
    return true;
  }
  return false;
}

export async function isSameDevice({ newDeviceId, oldDeviceId, oldGroupId, oldLabel, kind }: { newDeviceId?: string, oldDeviceId?: string, oldGroupId: string, oldLabel: string, kind: AudioOrVideo; }) {
  if (newDeviceId !== oldDeviceId) return false;
  if (kind === NAME.AUDIO && newDeviceId === MICROPHONE_DEFAULT) {
    return await isSameDefaultMicrophone(oldGroupId, oldLabel);
  }
  return true;
}
